/**
 * Family Plan Overview Component
 *
 * Displays:
 * - Tier badge (Drama+ or DramaX)
 * - Billing interval
 * - Daily quota
 * - Member count
 * - Invite button (admin only, if slots available)
 * - Plan full notice (admin only, if no slots)
 *
 * Presentational component: No business logic, only displays data
 */
import React from 'react';
import { View, Text, Pressable } from 'react-native';
import type { TFunction } from '@dramadetector/shared';

interface FamilyPlanOverviewProps {
  tier: 'drama_plus' | 'dramax';
  billingInterval: string;
  dailyQuota: number;
  memberCount: number;
  maxMembers: number;
  isFamilyAdmin: boolean;
  onInvitePress: () => void;
  t: TFunction<"subscription">;
  tCommon: TFunction<"common">; // For common namespace keys (monthly, yearly)
}

export function FamilyPlanOverview({
  tier,
  billingInterval,
  dailyQuota,
  memberCount,
  maxMembers,
  isFamilyAdmin,
  onInvitePress,
  t,
  tCommon,
}: FamilyPlanOverviewProps) {
  const slotsRemaining = maxMembers - memberCount;

  // Get tier badge text from translation (use explicit conditional, not template literal)
  const tierBadgeKey = tier === 'drama_plus' ? 'family.tierBadge.drama_plus' : 'family.tierBadge.dramax';

  return (
    <View className="bg-white p-6 shadow-sm mb-4">
      <Text className="text-2xl font-bold text-gray-900 mb-2">
        {t(tierBadgeKey as any)}
      </Text>
      <Text className="text-gray-600 mb-4">
        {t('family.billingInterval', {
          interval: billingInterval === 'yearly' ? tCommon('yearly') : tCommon('monthly'),
        })}
      </Text>

      {/* Quota and Members */}
      <View className="flex-row justify-between mb-4">
        <View className="flex-1 mr-2">
          <Text className="text-sm text-gray-500 mb-1">{t('family.dailyQuota')}</Text>
          <Text className="text-xl font-semibold text-teal-600">
            {dailyQuota}
          </Text>
        </View>
        <View className="flex-1 ml-2">
          <Text className="text-sm text-gray-500 mb-1">{t('family.members')}</Text>
          <Text testID="member-count" className="text-xl font-semibold text-gray-900">
            {memberCount} / {maxMembers}
          </Text>
        </View>
      </View>

      {/* Admin controls */}
      {isFamilyAdmin && slotsRemaining > 0 && (
        <Pressable
          testID="create-invitation"
          onPress={onInvitePress}
          className="bg-teal-600 rounded-xl py-3 px-4 active:opacity-80"
        >
          <Text className="text-white font-semibold text-center">
            {t('family.inviteMember')} ({slotsRemaining} {t('family.slotsLeft')})
          </Text>
        </Pressable>
      )}

      {isFamilyAdmin && slotsRemaining === 0 && (
        <View className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <Text className="text-amber-800 text-sm">
            {t('family.planFull')}
          </Text>
        </View>
      )}
    </View>
  );
}
